import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput'
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import { saveEvent, deleteEvent, checkLocationConflicts, buildLocationCheckRanges, describeLocationConflicts } from '@/data/events';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CustomField from '@/components/CustomFields/CustomField';
import Callout from '@/components/General/Callout';
import { serviceFlowStyles } from '@/components/Events/ServiceFlow/serviceFlowStyles';
import { isServiceFlowV2Available } from '@/config/serviceFlows';
import { Toast } from 'react-native-toast-message/lib/src/Toast';
import { areStringsPopulated } from '@/utils/InputValidator';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import RecurrenceSettingsNew from '@/components/Events/RecurrenceSettingsNew';
import { showAlert } from '@/utils/showAlert';
import SelectInput from '@/components/General/inputs/SelectInput';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import LocationScheduleModal from '@/components/Events/LocationScheduleModal';
import Unicon from '@/components/General/Unicon';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { StyleSheet } from 'react-native-unistyles';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import usePlatform from '@/utils/hooks/usePlatform';

const EMPTY_DRAFT = {};

export default function AddEventModal() {
    const [ index, setIndex ] = useState(0);
    const {id, defaultStartTime} = useLocalSearchParams();
    const storeKey = id || "new"

    const [ showRecurringOptions, setShowRecurringOptions ] = useState(false);
    const [ scheduleModalOpen, setScheduleModalOpen ] = useState(false);
    const [ locationConflicts, setLocationConflicts ] = useState([]);

    const renderTabBar = useMemo(() => props => <ReachTabBar {...props} scrollEnabled />, []);
    const { isNative } = usePlatform();

    const {theme: {colors, fonts}} = useUnistyles();
    const queryClient = useQueryClient();
    const router = useRouter()
    const pathname = usePathname()

    const { data: eventCustomFields } = Query.eventCustomFields.all()
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: eventToEdit } = Query.events.byId({id: id, options: {enabled: !!id, placeholderData: {}}})
    const { data: printLabels } = Query.printLabels.all()
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID })
    const serviceFlowV2 = isServiceFlowV2Available(organization)
    // Service Flow v2 attachment (Events.serviceFlowTemplateID) — the Event's
    // Save owns the binding change; the server applies it to future dates.
    const { data: serviceFlowAttachment, isError: attachmentError } = Query.serviceFlows.eventAttachment({
        organizationID: loggedInUser?.organizationID,
        accountID: loggedInUser?.accountID,
        eventID: id,
        options: { enabled: !!id && serviceFlowV2 },
    });
    const [serviceFlowAttachmentSeeded, setServiceFlowAttachmentSeeded] = useState(false);
    const { data: serviceFlowTemplatePage, isError: serviceFlowTemplatesError } = Query.serviceFlows.templates({
        organizationID: loggedInUser?.organizationID,
        accountID: loggedInUser?.accountID,
        archived: false,
        options: { enabled: serviceFlowV2 },
    });
    const serviceFlowTemplates = serviceFlowTemplatePage?.templates || [];
    const serviceFlowTemplateOptions = useMemo(() => ([
        { label: 'None', value: 'none', subtitle: 'New dates start with a blank flow.' },
        ...serviceFlowTemplates.map(t => ({ label: t.name, value: t.id, subtitle: `${t.itemCount ?? 0} item${(t.itemCount ?? 0) === 1 ? '' : 's'}` })),
    ]), [serviceFlowTemplates]);
    const serviceFlowLocationOptions = useMemo(() => ([
        { label: 'Inherit default', value: 'inherit', subtitle: 'Shares the default flow — edits and assignments stay shared.' },
        { separator: true },
        ...serviceFlowTemplateOptions,
    ]), [serviceFlowTemplateOptions]);
    const saveEventMutation = useMutation({
        mutationFn: saveEvent,
        onSuccess: () => {
            removeEditScreenData("AddEventModal", storeKey)
            queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.events.byId(id)});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.upcomingTasks()})
            queryClient.invalidateQueries({queryKey: ['dashboardData']})
            queryClient.invalidateQueries({queryKey: QueryKeys.serviceFlows.all()})
            redirectToDefault(router, "AddEventModal")
        },
        onError: (error) => {
            if (error?.serviceFlowLocations) setEditScreenData("AddEventModal", storeKey, { serviceFlowLocations: error.serviceFlowLocations });
            // Spec §12.3: when occurrence generation/attachment fails after the
            // Event row was created, retain the new Event ID in the draft so a
            // retry saves against that Event instead of inserting a duplicate.
            if (error?.eventID) {
                setEditScreenData("AddEventModal", storeKey, { id: error.eventID, serviceFlowSetupPending: true });
                Toast.show({ type: 'error', text2: 'The event was created but finishing setup failed — press Save to retry.' });
            }
        },
    })

    const deleteEventMutation = useMutation({
        mutationFn: deleteEvent,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.upcomingTasks()})
            queryClient.invalidateQueries({queryKey: ['dashboardData']})
            redirectToDefault(router, "AddEventModal")
        },
    })


    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const eventDetails = useEditScreenStore(s => s.AddEventModal?.[storeKey]) || EMPTY_DRAFT

    useEffect(() => {
        if(eventToEdit){
            if(Object.keys(eventToEdit).length > 0 && Object.keys(eventDetails).length === 0){
                const loadedEvent = {...eventToEdit}

                // Derive selected volunteer teams from event tasks
                const teamMap = new Map();
                loadedEvent.Tasks?.forEach(t => {
                    if (t.volunteerTeamID && t.VolunteerTeam) {
                        teamMap.set(t.volunteerTeamID, t.VolunteerTeam);
                    }
                });

                setShowRecurringOptions(loadedEvent.isRecurring);
                setEditScreenData("AddEventModal", storeKey, {
                    ...loadedEvent,
                    VolunteerTeams: Array.from(teamMap.values()),
                    Locations: loadedEvent.Locations?.map(l => ({...l.Location, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, checkInFilterID: l.checkInFilterID, CheckInFilter: l.CheckInFilter})),
                    defaultStartTime: loadedEvent.defaultStartTime ? new Date(loadedEvent.defaultStartTime) : null,
                    defaultEndTime: loadedEvent.defaultEndTime ? new Date(loadedEvent.defaultEndTime) : null,
                    recurringReferenceDate: loadedEvent.recurringReferenceDate ? new Date(loadedEvent.recurringReferenceDate) : null,
                    recurrenceEndDate: loadedEvent.recurrenceEndDate ? new Date(loadedEvent.recurrenceEndDate) : null,
                    recurringInterval: loadedEvent.recurringInterval ? loadedEvent.recurringInterval.toString() : null,
                    originalValues: loadedEvent,
                })
            }
        }
    }, [eventToEdit])

    // Seed the current template attachment into the draft when editing (v2).
    useEffect(() => {
        if (id && eventDetails.id && serviceFlowAttachment && !serviceFlowAttachmentSeeded) {
            setEventDetails({
                ...('serviceFlowTemplateVersion' in eventDetails ? {} : {
                serviceFlowTemplateID: serviceFlowAttachment.serviceFlowTemplateID ?? null,
                serviceFlowBindingVersion: serviceFlowAttachment.serviceFlowBindingVersion ?? 0,
                serviceFlowTemplateVersion: serviceFlowAttachment.ServiceFlowTemplate?.version ?? null,
                }),
                serviceFlowLocations: eventDetails.serviceFlowLocations ?? (serviceFlowAttachment.locations ?? []).map(b =>
                    // No binding row exists when bindingVersion is 0 — surface
                    // implicit inherit as the explicit 'None' default so every
                    // existing location starts unset until assigned manually.
                    !b.isOverride && (b.bindingVersion ?? 0) === 0 ? { ...b, isOverride: true, templateID: null } : b),
            });
            setServiceFlowAttachmentSeeded(true);
        }
    }, [id, eventDetails.id, serviceFlowAttachment, serviceFlowAttachmentSeeded]);

        
    const setEventDetails = (details) => {
        setEditScreenData("AddEventModal", storeKey, details)
    };

    useEffect(() => {
        if(defaultStartTime){
            // Convert string datetime to Date object if needed to prevent timezone errors
            const startTimeDate = typeof defaultStartTime === 'string' ? new Date(defaultStartTime) : defaultStartTime;
            setEventDetails({defaultStartTime: startTimeDate})
        }
    }, [defaultStartTime]);

    // Debounced heads-up: warn (don't block) when a selected location is
    // already booked at the draft times. Uses the same ranges saveEvent will
    // hard-check, so the warning matches what would block the save.
    const toISOorNull = (v) => (v && !isNaN(new Date(v)) ? new Date(v).toISOString() : null);
    const conflictCheckKey = JSON.stringify({
        l: (eventDetails.Locations || []).map(l => l?.id),
        d: eventDetails.defaultLocationID || null,
        s: toISOorNull(eventDetails.defaultStartTime),
        e: toISOorNull(eventDetails.defaultEndTime),
        a: !!eventDetails.defaultAllDay,
        r: !!eventDetails.isRecurring,
        rr: eventDetails.rruleString || null,
        re: toISOorNull(eventDetails.recurrenceEndDate),
    });

    useEffect(() => {
        const locationIDs = [...(eventDetails.Locations || []).map(l => l?.id), eventDetails.defaultLocationID].filter(Boolean);
        const ranges = buildLocationCheckRanges({
            startTime: eventDetails.defaultStartTime,
            endTime: eventDetails.defaultEndTime,
            allDay: eventDetails.defaultAllDay,
            isRecurring: eventDetails.isRecurring,
            rruleString: eventDetails.rruleString,
            recurrenceEndDate: eventDetails.recurrenceEndDate,
        });
        if (!locationIDs.length || !ranges.length) {
            setLocationConflicts([]);
            return;
        }
        let cancelled = false;
        const t = setTimeout(() => {
            checkLocationConflicts({ locationIDs, ranges, excludeEventID: id || null })
                .then((conflicts) => { if (!cancelled) setLocationConflicts(conflicts); });
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [conflictCheckKey, id]);


    const calcEndDate = (startDate, add_hours, add_minutes = 0, returnValue = false) => {
        if(!startDate){
            return false;
        }

        let endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + parseInt(add_hours));
        endDate.setMinutes(endDate.getMinutes() + parseInt(add_minutes));

        if(returnValue){
            return endDate;
        }
        else{
            setEventDetails({
                defaultEndTime: endDate
            })
        }
    }


    const customFields = eventCustomFields?.map((field) => {
        return <CustomField
            key={field.id}
            fieldType={field.type}
            required={field.required}
            onUpdate={(new_value) => {
                let old_custom_values = eventDetails.defaultCustomFieldValues ? eventDetails.defaultCustomFieldValues : {}

                setEventDetails({
                    defaultCustomFieldValues: {
                        ...old_custom_values,
                        [field.name]: new_value
                    }
                })
            }}
            options={{
                title: field.name,
                value: eventDetails.defaultCustomFieldValues?.[field.name]
            }}
        />
    });


    const printOptionComponents = useMemo(() => eventDetails.Locations?.map((location) => {
        return <View key={location.id} style={{paddingTop:10, paddingBottom:10}}>

            <Text style={{fontFamily:fonts.heading, color:colors.text, fontSize:14}}>{location.name}</Text>
            <View style={{flexDirection:"row", alignItems:"center", justifyContent:"center", gap:10}}>
                <View style={{width:"50%"}}>
                    <SelectInput 
                        title="Individual Label"
                        value={printLabels?.find(l => l.id == eventDetails.Locations?.find(l => l.id == location.id)?.printLabelID)?.name}
                        options={printLabels?.map(label => label.name)}
                        onUpdate={(new_value) => {
                            const labelID = printLabels.find(l => l.name == new_value)?.id;
                            setEventDetails({ Locations: eventDetails.Locations.map(l => l.id == location.id ? {...l, printLabelID: labelID} : l)})
                        }}
                    />
                </View>
                <View style={{width:"50%"}}>
                    <SelectInput 
                        title="Family Label"
                        value={printLabels?.find(l => l.id == eventDetails.Locations?.find(l => l.id == location.id)?.printFamilyLabelID)?.name}
                        options={printLabels?.map(label => label.name)}
                        onUpdate={(new_value) => {
                            const labelID = printLabels.find(l => l.name == new_value)?.id;
                            setEventDetails({ Locations: eventDetails.Locations.map(l => l.id == location.id ? {...l, printFamilyLabelID: labelID} : l)})
                        }}
                    />
                </View>
            </View>
        </View>
    }), [eventDetails.Locations, printLabels])

    const volunteerTasks = useMemo(() => eventDetails.Tasks?.filter(t => t?.dueType == "EVENT_VOLUNTEER") || [], [eventDetails.Tasks])
    const selectedVolunteerTeams = useMemo(() => eventDetails.VolunteerTeams || [], [eventDetails.VolunteerTeams])

    const DetailsSection = () => (
        <View key="details" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                <View style={styles.scrollContent}>
                    {id &&
                        <View style={styles.calloutWrapper}>
                            <Callout type="info" title="You are editing the entire Event Series" message="Edits to the Event Series will not update any past occurences. Only future occurences will be affected."/>
                        </View>
                    }

                    <InputSection style={{marginHorizontal:5}} title="Basic Info" description="Enter the general information for your event.">
                        <Input
                            key="name"
                            title="Event Name"
                            maxLength={120}
                            value={eventDetails.name}
                            onUpdate={(new_value) => {
                                setEventDetails({
                                    name: new_value
                                })
                            }}
                        />

                        <SheetSelector type="calendars" title="Calendar" addScreenName="events/calendars/create" fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.Calendar} onChange={(selected_item) => {
                            setEventDetails({
                                calendarID: selected_item?.id,
                                Calendar: selected_item
                            });
                        }} />

                        <SheetSelector type="people" title="Speaker" addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.defaultSpeaker} onChange={(selected_item) => {
                            setEventDetails({
                                defaultSpeakerID: selected_item?.id,
                                defaultSpeaker: selected_item
                            });
                        }} />

                        <Input
                            key="notes"
                            title="Notes"
                            detection={["mention", "tag", "link"]}
                            value={eventDetails.defaultNotes}
                            multiline
                            onUpdate={(new_value) => {
                                setEventDetails({
                                    defaultNotes: new_value
                                })
                            }}
                        />
                    </InputSection>

                    <InputSection style={{marginHorizontal:5}} title="Date & Time" description="Choose what time this event will start and end on each occurence.">
                        <ToggleInput
                            selectedValues={[eventDetails.defaultAllDay ? "All Day" : "Time Slot"]}
                            items={["Time Slot", "All Day"]}
                            onItemChanged={(selected) => {
                                setEventDetails({
                                    defaultAllDay: selected == 'All Day',
                                })
                            }}
                        />

                        <DatePickerInput
                            title="Start Time"
                            mode={eventDetails.defaultAllDay ? "date" : "datetime"}
                            value={eventDetails.defaultStartTime}
                            minuteInterval={5}
                            onUpdate={(new_value) => {
                                setEventDetails({
                                    recurringReferenceDate: new_value,
                                    defaultStartTime: new_value,
                                    defaultEndTime: eventDetails.defaultEndTime && eventDetails.defaultEndTime < new_value ?  calcEndDate(new_value, 0, 30, true) : eventDetails.defaultEndTime
                                })
                            }}
                        />

                        <DatePickerInput
                            title="End Time"
                            mode={eventDetails.defaultAllDay ? "date" : "datetime"}
                            minimumDate={eventDetails.defaultStartTime}
                            value={eventDetails.defaultEndTime}
                            minuteInterval={5}
                            onUpdate={(new_value) => {
                                setEventDetails({
                                    defaultEndTime: new_value
                                })
                            }}
                        />

                        { !eventDetails.defaultAllDay &&
                            <View style={styles.quickTimeRow}>
                                <TouchableOpacity onPress={() => calcEndDate(eventDetails.defaultStartTime, 0, 30)} style={styles.quickTimeButton}><Text style={styles.quickTimeText}>+30m</Text></TouchableOpacity>
                                <TouchableOpacity onPress={() => calcEndDate(eventDetails.defaultStartTime, 1, 0)} style={styles.quickTimeButton}><Text style={styles.quickTimeText}>+1hr</Text></TouchableOpacity>
                                <TouchableOpacity onPress={() => calcEndDate(eventDetails.defaultStartTime, 2, 0)} style={styles.quickTimeButton}><Text style={styles.quickTimeText}>+2hr</Text></TouchableOpacity>
                                <TouchableOpacity onPress={() => calcEndDate(eventDetails.defaultStartTime, 4, 0)} style={styles.quickTimeButton}><Text style={styles.quickTimeText}>+4hr</Text></TouchableOpacity>
                                <TouchableOpacity onPress={() => calcEndDate(eventDetails.defaultStartTime, 24, 0)} style={styles.quickTimeButton}><Text style={styles.quickTimeText}>+24hr</Text></TouchableOpacity>
                            </View>
                        }
                    </InputSection>
                </View>
            </KeyboardAwareScrollView>
        </View>
    );

    const RecurrenceSection = () => (
        <View key="recurrence" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                <View style={styles.scrollContent}>
                    <InputSection style={{marginHorizontal:5}} title="Recurrence">
                        <ToggleInput
                            selectedValues={[eventDetails.isRecurring ? "Recurring" : "One-Time"]}
                            items={["One-Time", "Recurring"]}
                            onItemChanged={(selected) => {
                                setEventDetails({
                                    isRecurring: selected == 'Recurring'
                                });
                                setShowRecurringOptions(selected == 'Recurring');
                            }}
                        />
                    </InputSection>

                    <RecurrenceSettingsNew isShowing={showRecurringOptions} eventDetails={eventDetails} setEventDetails={setEventDetails} />
                </View>
            </KeyboardAwareScrollView>
        </View>
    );

    const TasksSection = () => {
        const serviceFlowPickerLocations = [
            ...(eventDetails.Locations || []),
            ...(serviceFlowAttachment?.locations || [])
                .filter(b => !eventDetails.Locations?.some(l => l.id === b.locationID))
                .map(b => ({ id: b.locationID, name: b.locationName ?? 'Location' })),
        ];
        const updateLocationBinding = (location, binding, value) => {
            const others = (eventDetails.serviceFlowLocations || []).filter(b => b.locationID !== location.id);
            const base = { locationID: location.id, bindingVersion: binding?.bindingVersion ?? 0 };
            const next = value === 'inherit'
                // explicit forces a real row when none exists yet — otherwise
                // reopening the event would show 'None' instead of inherit.
                ? { ...base, isOverride: false, templateID: null, explicit: !base.bindingVersion }
                : value === 'none'
                    ? { ...base, isOverride: true, templateID: null }
                    : { ...base, isOverride: true, templateID: value, templateVersion: serviceFlowTemplates.find(t => t.id === value)?.version ?? null };
            setEventDetails({ serviceFlowLocations: [...others, next] });
        };
        const selectedDefaultTemplate = eventDetails.serviceFlowTemplateID;
        return (
        <View key="tasks" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                <View style={styles.scrollContent}>
                    <InputSection style={{marginHorizontal:5}} title="Service Flow" description="Pick the service flow template new dates of this event should receive. Each date still gets its own editable flow.">
                        {serviceFlowV2 ? (
                            <View style={{ gap: 4 }}>
                            {(attachmentError || serviceFlowTemplatesError) && <Callout type="error" message="Could not load Service Flow templates. Reopen this form to retry." />}
                            <SelectInput
                                title="Default service flow"
                                disabled={!!id && !serviceFlowAttachmentSeeded}
                                value={!selectedDefaultTemplate ? 'none' : { value: selectedDefaultTemplate, label: serviceFlowTemplates.find(t => t.id === selectedDefaultTemplate)?.name ?? serviceFlowAttachment?.ServiceFlowTemplate?.name ?? selectedDefaultTemplate }}
                                onUpdate={(value) => setEventDetails({
                                    serviceFlowTemplateID: value === 'none' ? null : value,
                                    serviceFlowTemplateVersion: value === 'none' ? null : serviceFlowTemplates.find(t => t.id === value)?.version ?? null,
                                })}
                                options={serviceFlowTemplateOptions}
                                hideClearButton
                            />
                            {!!loggedInUser?.can?.('Edit Events') && (
                                <TouchableOpacity onPress={() => router.push('/events/service-flow')} testID="service-flow-template-manage-link">
                                    <Text style={serviceFlowStyles.notesToggleText}>Manage templates</Text>
                                </TouchableOpacity>
                            )}
                            </View>
                        ) : (
                            <Callout type="info" message="Service Flow templates are being rolled out to your organization. Dates keep using the current schedule in the meantime." />
                        )}
                    </InputSection>

                    {serviceFlowV2 && serviceFlowPickerLocations.length > 0 && (
                        <InputSection style={{marginHorizontal:5}} title="Location Service Flows" description="Each location can use its own service flow template, or choose Inherit default to share the flow above — assignments and edits are shared too.">
                            {serviceFlowPickerLocations.map(location => {
                                const binding = eventDetails.serviceFlowLocations?.find(b => b.locationID === location.id);
                                const boundTemplateName = serviceFlowTemplates.find(t => t.id === binding?.templateID)?.name ?? binding?.ServiceFlowTemplate?.name;
                                const value = !binding ? 'none' : binding.isOverride ? (!binding.templateID ? 'none' : { value: binding.templateID, label: boundTemplateName ?? binding.templateID }) : 'inherit';
                                return <View key={location.id} testID={`service-flow-location-template-${location.id}`}>
                                    <SelectInput
                                        title={location.name}
                                        disabled={!!id && !serviceFlowAttachmentSeeded}
                                        value={value}
                                        onUpdate={(v) => updateLocationBinding(location, binding, v)}
                                        options={serviceFlowLocationOptions}
                                        hideClearButton
                                    />
                                </View>;
                            })}
                        </InputSection>
                    )}

                    <InputSection style={{marginHorizontal:5}} title="Volunteering Teams" description="All tasks from the selected teams will be created for each occurrence of this event automatically.">
                        <SheetSelector type="taskTeams" title="Volunteering Teams" fromScreen={pathname} fromStoreKey={storeKey} addScreenName="tasks/teams/create" multiple value={selectedVolunteerTeams} onChange={(selectedTeams) => {
                            const selectedTeamIDs = new Set(selectedTeams.map(t => t.id));
                            // Keep existing volunteer tasks for teams that remain selected so their
                            // EventTasks/TaskInstances aren't dropped on save. Only build fresh tasks
                            // for teams that don't already have tasks loaded (e.g. newly added teams).
                            const keptVolunteerTasks = volunteerTasks.filter(t => selectedTeamIDs.has(t.volunteerTeamID));
                            const teamsWithTasks = new Set(keptVolunteerTasks.map(t => t.volunteerTeamID));
                            const newVolunteerTasks = [];
                            selectedTeams.forEach(team => {
                                if (teamsWithTasks.has(team.id)) return;
                                team.VolunteerTeamTasks?.forEach(vtt => {
                                    newVolunteerTasks.push({
                                        ...vtt.Task,
                                        dueType: 'EVENT_VOLUNTEER',
                                        volunteerTeamID: team.id,
                                        position: vtt.position,
                                    });
                                });
                            });
                            setEventDetails({
                                Tasks: [...keptVolunteerTasks, ...newVolunteerTasks],
                                VolunteerTeams: selectedTeams
                            });
                        }} />
                    </InputSection>

                </View>
            </KeyboardAwareScrollView>
        </View>
        );
    };

    // Shared by the SheetSelector and the room-schedule modal so the
    // defaultLocationID fix-up stays in one place.
    const applyLocations = (selected_items) => {
        const newDetails = { Locations: selected_items };
        if (selected_items?.length === 0) {
            newDetails.defaultLocationID = null;
        } else if (selected_items?.length === 1) {
            newDetails.defaultLocationID = selected_items[0]?.id;
        } else if (eventDetails.defaultLocationID && !selected_items?.find(l => l.id === eventDetails.defaultLocationID)) {
            newDetails.defaultLocationID = selected_items[0]?.id;
        }
        setEventDetails(newDetails);
    };

    const toggleLocationFromSchedule = (loc) => {
        const current = eventDetails.Locations || [];
        const exists = current.find(l => l.id === loc.id);
        applyLocations(exists ? current.filter(l => l.id !== loc.id) : [...current, loc]);
    };

    const LocationsSection = () => (
        <View key="locations" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                <View style={styles.scrollContent}>
                    <InputSection style={{marginHorizontal:5}} title="Locations" description="Select where this event takes place.">
                        <SheetSelector type="locations" multiple title="Locations" addScreenName="events/locations/create" fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.Locations} onChange={(selected_items) => {
                            applyLocations(selected_items);
                        }} />

                        <TouchableOpacity testID="view-room-schedule" style={styles.scheduleLink} onPress={() => setScheduleModalOpen(true)}>
                            <Unicon name="calendar-outline" size={15} colorVariant="primary" />
                            <Text style={styles.scheduleLinkText}>View room schedule</Text>
                        </TouchableOpacity>

                        {locationConflicts.length > 0 && (
                            <View testID="location-conflict-warning" style={{ marginTop: 8 }}>
                                <Callout
                                    type="warning"
                                    title="Location conflict"
                                    message={`Another event is already booked in this location:\n${describeLocationConflicts(locationConflicts)}`}
                                />
                            </View>
                        )}

                        {eventDetails.Locations?.length > 1 && (
                            <SelectInput
                                title="Default Location"
                                helpText="This location will be pre-selected when checking in attendees for this event"
                                value={{label: eventDetails.Locations?.find(l => l.id === eventDetails.defaultLocationID)?.name, value: eventDetails.defaultLocationID}}
                                options={eventDetails.Locations ? eventDetails.Locations?.map(l => ({ label: l.name, value: l.id })) : []}
                                onUpdate={(new_value) => {
                                    setEventDetails({ defaultLocationID: new_value });
                                }}
                            />
                        )}
                    </InputSection>

                    <InputSection style={{marginHorizontal:5}} title="Check-In Filters" description="Select a filter for each location to limit the list of people available during check-in. Only individuals matching the filter will appear in the suggested list.">
                        {(!eventDetails.Locations || eventDetails.Locations?.length == 0) && (
                            <Callout type="info" title="No locations added yet" message="Add a location above to configure check-in filters."/>
                        )}

                        {eventDetails.Locations?.map((location) => (
                            <View key={location.id}>
                                <SheetSelector
                                    type="quickFilters"
                                    title={`${location.name} Filter`}
                                    fromScreen={pathname}
                                    fromStoreKey={storeKey}
                                    value={location.CheckInFilter}
                                    onChange={(selected_item) => {
                                        setEventDetails({
                                            Locations: eventDetails.Locations.map(l =>
                                                l.id === location.id
                                                    ? {...l, checkInFilterID: selected_item?.id, CheckInFilter: selected_item}
                                                    : l
                                            )
                                        });
                                    }}
                                />
                            </View>
                        ))}
                    </InputSection>

                    <InputSection style={{marginHorizontal:5}} title="Print Labels" description="Print labels for each person checked in. You will need a Dymo LabelWriter printer connected to your computer.">
                        {
                            (!eventDetails.Locations || eventDetails.Locations?.length == 0) &&
                            <Callout type="info" title="No locations added yet" message="Add a location above to configure print labels."/>
                        }

                        {printOptionComponents}
                    </InputSection>
                </View>
            </KeyboardAwareScrollView>
        </View>
    );

    const SettingsSection = () => (
        <View key="settings" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                <View style={styles.scrollContent}>
                    <InputSection style={{marginHorizontal:5}} title="Integrations">
                        <ToggleRow
                            label="Auto Link Video Archive"
                            description="Periodically search your Youtube channel for videos of this event and link them to each instance."
                            onValueChange={(newVal) => setEventDetails({
                                autoFindVideoArchives: newVal
                            })}
                            value={eventDetails?.autoFindVideoArchives}
                        />

                        <ToggleRow
                            label="Service Flow in External Calendar"
                            description="Include service flow tasks in your Apple/Google calendar feed, showing the task, time, and assignee."
                            onValueChange={(newVal) => setEventDetails({
                                includeServiceFlowInExternalCalendar: newVal
                            })}
                            value={eventDetails?.includeServiceFlowInExternalCalendar}
                        />
                    </InputSection>

                    {
                        customFields?.length == 0
                        ? null
                        : <InputSection style={{marginHorizontal:5}} title="Custom Fields" description="Enter the default values for your Custom Event Fields. These can be changed for each occurrence.">
                            {customFields}
                        </InputSection>
                    }

                    {id &&
                        <TouchableOpacity style={styles.deleteButton} onPress={() => {
                            showAlert({
                                title: 'Delete Event',
                                subtitle: 'Are you sure you want to delete this event? This cannot be undone.',
                                confirmText: 'Delete',
                                isDestructive: true,
                                onConfirm: () => deleteEventMutation.mutate(eventDetails.id)
                            })
                        }}>
                            <Text style={styles.deleteButtonText}>Delete This Event</Text>
                        </TouchableOpacity>
                    }
                </View>
            </KeyboardAwareScrollView>
        </View>
    );

    const detailsSectionRef = useRef(DetailsSection);
    detailsSectionRef.current = DetailsSection;

    const recurrenceSectionRef = useRef(RecurrenceSection);
    recurrenceSectionRef.current = RecurrenceSection;

    const tasksSectionRef = useRef(TasksSection);
    tasksSectionRef.current = TasksSection;

    const locationsSectionRef = useRef(LocationsSection);
    locationsSectionRef.current = LocationsSection;

    const settingsSectionRef = useRef(SettingsSection);
    settingsSectionRef.current = SettingsSection;

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'Details':
                return detailsSectionRef.current();
            case 'Recurrence':
                return recurrenceSectionRef.current();
            case 'Tasks':
                return tasksSectionRef.current();
            case 'Locations':
                return locationsSectionRef.current();
            case 'Settings':
                return settingsSectionRef.current();
            default:
                return null;
        }
    }, []);

    const routes = useMemo(() => [
        {key: "Details", title: "Details"},
        {key: "Recurrence", title: "Recurrence"},
        {key: "Tasks", title: "Tasks"},
        {key: "Locations", title: "Locations"},
        {key: "Settings", title: "Settings"},
    ], []);

    return (
        <ModalWrapper
            noPadding
            title={id ? "Edit Event" : "New Event"}
            loading={saveEventMutation.isPending}
            onCancel={() => removeEditScreenData("AddEventModal", storeKey)} 
            onConfirm={() => {
                if (id && serviceFlowV2 && (!serviceFlowAttachmentSeeded || attachmentError)) return Toast.show({ type: 'error', text2: 'Service Flow templates must load before saving. Reopen this form to retry.' });
                if(!eventDetails.calendarID){
                    Toast.show({type: 'error',text2: `You must choose a calendar for your event to go to!`})
                    return true;
                }else if(!eventDetails.defaultStartTime){
                    Toast.show({type: 'error',text2: `You must select a start date or time for this Event!`})
                    return true;
                }else if(!areStringsPopulated([eventDetails.name])){
                    Toast.show({type: 'error',text2: `You must give your event a name!`})
                    return true;
                }else if(eventDetails.isRecurring && !eventDetails.rruleString){
                    Toast.show({type: 'error',text2: `For recurring events, please select a recurrence pattern`})
                    return true;
                }

                const missingRequiredFields = (eventCustomFields || []).filter(f => f.required && !f.isDeleted);
                const missingField = missingRequiredFields.find(f => {
                    const val = eventDetails.defaultCustomFieldValues?.[f.name];
                    return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                });
                if(missingField){
                    Toast.show({type: 'error', text2: `Please fill out the required field: ${missingField.name}`})
                    return true;
                }

                if(eventDetails.isRecurring){
                    Toast.show({type: 'info',text2: `Saving your event... If you connected a lot of tasks, this might take a minute.`})
                }
                
                const { originalValues, ...detailsWithoutOriginal } = eventDetails;
                saveEventMutation.mutate({details: detailsWithoutOriginal, originalValues, creatorID: loggedInUser?.id})
            }}
        >
            <View style={{flex:1}}>
                <LocationScheduleModal
                    visible={scheduleModalOpen}
                    onClose={() => setScheduleModalOpen(false)}
                    date={eventDetails.defaultStartTime || new Date()}
                    selectedLocationIDs={(eventDetails.Locations || []).map(l => l.id)}
                    onToggleLocation={toggleLocationFromSchedule}
                    selectable
                    highlightStart={eventDetails.defaultStartTime}
                    highlightEnd={eventDetails.defaultEndTime}
                    highlightAllDay={!!eventDetails.defaultAllDay}
                />
                <TabView
                    swipeEnabled={isNative}
                    renderTabBar={renderTabBar}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    onIndexChange={setIndex}
                    lazy
                    lazyPreloadDistance={0}
                />
            </View>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    scrollContent: {
        paddingBottom: 20,
        width: '100%',
    },
    calloutWrapper: {
        paddingHorizontal: 4,
        paddingTop: 8,
    },
    quickTimeRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        paddingTop: 12,
        gap: 8,
    },
    quickTimeButton: {
        borderRadius: 8,
        paddingVertical: 6,
        paddingHorizontal: 10,
        minWidth: 52,
        alignItems: 'center',
        backgroundColor: colors.surfaceVariant,
        borderWidth: 1,
        borderColor: colors.border,
    },
    quickTimeText: {
        color: colors.text,
        fontSize: 12,
        fontFamily: fonts.heading,
    },
    scheduleLink: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 6,
        paddingVertical: 6,
        paddingHorizontal: 4,
    },
    scheduleLinkText: {
        color: colors.primary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    deleteButton: {
        paddingVertical: 16,
        marginTop: 8,
        marginBottom: 12,
        alignItems: 'center',
    },
    deleteButtonText: {
        color: colors.error,
        fontFamily: fonts.heading,
        fontSize: 15,
    },
}))